import { useDialogForm } from '@mews-commander/form';
import { lt } from '@mews/framework';
import { Button, EmptyStateProps, IconName, Table, TableHeader, TableProvider, TableState } from '@optimus-web/core';
import { ContractsTextKey as TextKey } from '../../../ContractsTextKey';
import { UploadFileForm } from './forms/UploadFileForm';
import { useAttachmentLibraryTableColumns } from './hooks/useAttachmentLibraryTableColumns';
import { AttachmentLibraryTableData, AttachmentLibraryTableRowData } from './types';

interface AttachmentsTableProps {
    data: AttachmentLibraryTableData;
    loading: boolean;
    tableState: TableState<AttachmentLibraryTableRowData>;
    onTableStateChange: (newState: TableState<AttachmentLibraryTableRowData>) => void;
}

export const AttachmentLibraryTable = ({ data, loading, tableState, onTableStateChange }: AttachmentsTableProps) => {
    const [renderUploadForm, toggleUploadForm] = useDialogForm({
        title: lt(TextKey.ContractsUploadFileLabel),
        renderForm: (props) => <UploadFileForm {...props} />,
        size: 'small',
    });

    const columns = useAttachmentLibraryTableColumns();

    const emptyStateProps = {
        primaryText: lt('ContractsNoAttachmentsFoundPrimaryText'),
        secondaryText: lt('ContractsNoAttachmentsFoundSecondaryText'),
        action: (
            <Button icon={IconName.Upload} variant="primary" type="button" onClick={toggleUploadForm}>
                {lt('ContractsUploadFileLabel')}
            </Button>
        ),
        size: 'large',
        pictogram: 'search',
    } satisfies EmptyStateProps;

    return (
        <>
            <TableProvider columns={columns} state={tableState} onStateChange={onTableStateChange} as="div">
                <TableHeader
                    title={lt('ContractsAttachmentLibraryTitle')}
                    globalSearch
                    rightSide={
                        <Button
                            icon={IconName.Upload}
                            size="small"
                            variant="tertiary"
                            type="button"
                            onClick={toggleUploadForm}
                        >
                            {lt('ContractsUploadFileLabel')}
                        </Button>
                    }
                />
                <Table
                    fixedFirstColumn
                    uniqueRowId="id"
                    data={data}
                    amountOfRows={data?.length ?? 0}
                    loading={loading}
                    emptyState={emptyStateProps}
                />
            </TableProvider>
            {renderUploadForm()}
        </>
    );
};
